/**
 * Component Name: InvoiceManagement
 * Description: The main component for the invoice management module, integrating project hierarchy, header, filter modal, and table/chart displays.
 * Props:
 *   - projectModule (boolean): Indicates if the user has access to the project module.
 *   - setSidebarCollapsed (function, optional): Callback to collapse the sidebar.
 * Feature: Manages the invoice management interface by fetching and filtering invoice data, rendering project hierarchy, header with breadcrumbs and stats, filter modal, and dynamic tables/charts based on grouping and drill-down state.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HeaderSection from '../../../components/invoice-management/header-section';
import FilterModal from '../../../components/invoice-management/filter-modal';
import ProjectHierarchyHoverTree from '../../../components/invoice-management/project-hierarchy';
import ViewInvoiceModal from '../../../components/cost-breakdown/view-invoice-modal';
import { initialFilterFields } from '../../../constants/fields.constant';
import './invoiceManagement.scss';
import { handleApplyFilters } from '../utils/applyFilters';
import { CONTENT_HEADERS } from 'src/util/enums/invoiceManagementTableHeader';
import renderTable from 'src/components/invoice-management/render-table';
import { fetchInvoiceData as importedFetchInvoiceData } from '../utils/fetchInvoiceData';
import { TaskType } from '../../../util/enums';
import { INVOICE_CHART_DRILL_DOWN_STATE, INVOICE_ERROR_MESSAGES, INVOICE_GROUPING_VALUES, INVOICE_STATUS, TABLE_BREADCRUMB_ITEMS, TABLE_GROUP_TYPES, BREADCRUMB_ITEMS_TYPES } from '../../../util/enums/invoice-management';
import { handleBreadcrumbClick } from './utils/breadcrumbHandlers';
import { buildStageBreadcrumbPath, addBreadcrumbHandlers } from './utils/breadcrumbPathBuilder';

const InvoiceManagement = ({ projectModule, setSidebarCollapsed }) => {
    const dispatch = useDispatch();
    // Get finance settings from Redux
    const financeSettings = useSelector(state => state.globalSetting?.finance_tax || {});

    // Error handling state
    const [error, setError] = useState(null);

    if (typeof setSidebarCollapsed === 'function') setSidebarCollapsed(true);

    const [isInvoiceDetailsModalOpen, setIsInvoiceDetailsModalOpen] = useState(false);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [projects, setProjects] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [summaryStats, setSummaryStats] = useState({
        totalInvoices: 0,
        totalAmount: '0.00',
        totalSuppliers: 0,
        paymentStatus: INVOICE_STATUS.NA,
    });
    const [isHovering, setIsHovering] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);
    const panelRef = useRef(null);

    const [breadcrumbItems, setBreadcrumbItems] = useState([]);
    const [filterState, setFilterState] = useState(initialFilterFields);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('none');
    const [subTable, setSubTable] = useState(null);
    // state to manage the chart's drill-down independently but allow synchronization
    const [chartDrillDownState, setChartDrillDownState] = useState(null);

    // Initiates API calls to retrieve invoice and project data, updating state accordingly.
    const fetchInvoiceData = useCallback(async () => {
        try {
            // Fetches invoice and project data from the API, updating state with invoices, projects, and filter options.
            await importedFetchInvoiceData(
                dispatch,
                initialFilterFields,
                setProjects,
                setInvoices,
                setSummaryStats,
                setFilterState,
                setBreadcrumbItems
            );
        } catch (error) {
            console.error('Error fetching invoice data:', error);
            setError(INVOICE_ERROR_MESSAGES.LOAD_INVOICE_ERROR);
        }
    }, [dispatch]);

    // Applies current filters to invoices and updates summary statistics.
    const applyFilters = useCallback(() => {
        try {
            // Helper Function: Filters invoices based on criteria like supplier, status, and date, and calculates summary statistics.
            const {
                tempInvoices,
                totalInvoices,
                totalAmount,
                totalSuppliers,
                totalPOs,
                paymentStatus
            } = handleApplyFilters(invoices, filterState, subTable, financeSettings);
            setFilteredInvoices(tempInvoices);
            setSummaryStats({
                totalInvoices,
                totalAmount,
                totalSuppliers,
                totalPOs,
                paymentStatus
            });
        } catch (error) {
            console.error('Error applying filters:', error);
            setError(INVOICE_ERROR_MESSAGES.APPLY_FILTER_ERROR);
        }
    }, [invoices, filterState, subTable, financeSettings]);

    useEffect(() => {
        applyFilters();
    }, [invoices, filterState, subTable, applyFilters]);

    useEffect(() => {
        fetchInvoiceData();
    }, [fetchInvoiceData]);

    // Handles the application of filters and closes the filter modal.
    const handleFilterChange = (selectedOption, actionMeta) => {
        const name = actionMeta ? actionMeta.name : selectedOption.target.name;
        const value = actionMeta ? selectedOption : selectedOption.target.value;

        setFilterState((prevState) => ({
            ...prevState,
            [name]: {
                ...prevState[name],
                value,
            },
        }));
    };

    // Applies new filter state and closes the filter modal.
    const handleApplyFiltersAndCloseModal = (newFilterState) => {
        setFilterState(newFilterState);
        setIsFilterModalOpen(false);
    };

    // --- Project Hierarchy Hover Tree Handlers ---
    // Opens the invoice details modal for a selected invoice.
    const handleViewInvoiceDetailsModal = (invoiceId) => {
        const invoice = filteredInvoices.find((inv) => inv.invoiceId === invoiceId);
        setSelectedInvoice(invoice);
        setIsInvoiceDetailsModalOpen(true);
    };

    // --- Chart Drill-down Handlers ---
    // Handles chart bar clicks to update drill-down state and breadcrumbs.
    const handleChartBarClick = useCallback((type, id, label, projectId = null, stageId = null, originalGroup = null) => {
        switch (type) {
            case INVOICE_CHART_DRILL_DOWN_STATE.PROJECT:
                // Drill down chart to show POs for this project, and update table
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId: id, label: label, originalGroup: originalGroup || selectedGroup });
                handleProjectClick(id, label); // Trigger table drill-down
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.PROJECT_DETAILS:
                // Drill down chart to show POs for this project, and update table
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId: id, label: label, originalGroup: originalGroup || selectedGroup });
                handleProjectClick(id, label); // Trigger table drill-down
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.PO_INVOICES:
                // Drill down chart to show invoices for this PO, and update table
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: originalGroup || selectedGroup, projectId: projectId, stageId: stageId });
                handlePOClick(id, projectId, stageId); // Trigger table drill-down
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS:
                // Go back to PO details view (bar chart showing POs)
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId: projectId, stageId: stageId, label: label, originalGroup: originalGroup || selectedGroup });
                if (stageId) {
                    // when coming from section drill-down
                    handleSectionClick(stageId, label, projectId);
                } else {
                    // when coming from project drill-down
                    handleProjectClick(projectId, label);
                }
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.SUPPLIER:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.SUPPLIER });
                handleSupplierClick(id, label);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.SUPPLIER_INVOICES:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.SUPPLIER });
                handleSupplierClick(id, label);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.MONTH:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.MONTH });
                handlePeriodClick(id, label, INVOICE_GROUPING_VALUES.MONTH);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.MONTH_INVOICES:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.MONTH });
                handlePeriodClick(id, label, INVOICE_GROUPING_VALUES.MONTH);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.QUARTER:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.QUARTER });
                handlePeriodClick(id, label, INVOICE_GROUPING_VALUES.QUARTER);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.QUARTER_INVOICES:
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: id, label: label, originalGroup: INVOICE_GROUPING_VALUES.QUARTER });
                handlePeriodClick(id, label, INVOICE_GROUPING_VALUES.QUARTER);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.SECTION:
                // For section, drill down chart to PO details, and update table
                setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId: projectId, stageId: id, label: label, originalGroup: originalGroup || selectedGroup });
                handleSectionClick(id, label, projectId);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.VIEW_INVOICE_MODAL:
                // This case is for the final drill-down to show the invoice modal
                handleViewInvoiceDetailsModal(id);
                break;
            case INVOICE_CHART_DRILL_DOWN_STATE.GROUP:
                // Go back to top-level grouping for the chart, and reset table
                setChartDrillDownState(null);
                setSubTable(null); // Reset table to top-level group view
                setSelectedGroup(id || INVOICE_GROUPING_VALUES.NONE); // ID here is the group type (project, po, etc.)
                // Reset breadcrumbs to the top-level grouping
                let breadcrumbLabel = '';
                let breadcrumbType = '';
                switch (id) {
                    case INVOICE_CHART_DRILL_DOWN_STATE.PROJECT:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.PROJECT;
                        breadcrumbType = TABLE_GROUP_TYPES.PROJECT;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.SECTION:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.SECTION;
                        breadcrumbType = TABLE_GROUP_TYPES.SECTION;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.PO:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.PO;
                        breadcrumbType = TABLE_GROUP_TYPES.PO;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.SUPPLIER:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.SUPPLIER;
                        breadcrumbType = TABLE_GROUP_TYPES.SUPPLIER;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.MONTH:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.MONTH;
                        breadcrumbType = TABLE_GROUP_TYPES.MONTH;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.QUARTER:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.QUARTER;
                        breadcrumbType = TABLE_GROUP_TYPES.QUARTER;
                        break;
                    case INVOICE_CHART_DRILL_DOWN_STATE.NONE:
                    default:
                        breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.INVOICE;
                        breadcrumbType = TABLE_GROUP_TYPES.NONE;
                        break;
                }
                setBreadcrumbItems([
                    { label: breadcrumbLabel, type: breadcrumbType, isActive: true },
                ]);
                break;
            default:
                console.warn('Unhandled chart bar click type:', type);
                break;
        }
    }, [handleViewInvoiceDetailsModal, projects, filteredInvoices, selectedGroup]);


    // Updates filter state and breadcrumbs when a project is selected from the hierarchy.
    const handleProjectSelect = useCallback((project) => {
        setFilterState((prevState) => ({
            ...initialFilterFields,
            supplierId: {
                ...initialFilterFields.supplierId,
                options: prevState.supplierId.options,
            },
            selectedProjectId: { ...initialFilterFields.selectedProjectId, value: project.id },
        }));
        setSubTable(null);
        setBreadcrumbItems([
            { label: project.name, value: project.id, type: BREADCRUMB_ITEMS_TYPES.PROJECT, isActive: true, id: project.id },
        ]);
        setIsHovering(false);
        setChartDrillDownState(null); // Reset chart drill down on project selection
    }, []);

    // Updates filter state and breadcrumbs when a stage is selected from the hierarchy.
    const handleStageSelect = useCallback((project, stage) => {
        setFilterState((prevState) => ({
            ...initialFilterFields,
            supplierId: {
                ...initialFilterFields.supplierId,
                options: prevState.supplierId.options,
            },
            selectedProjectId: { ...initialFilterFields.selectedProjectId, value: project.id },
            selectedStageId: { ...initialFilterFields.selectedStageId, value: stage.id },
        }));
        setSubTable(null);

        // Use the utility function to build complete breadcrumb path
        const breadcrumbPath = buildStageBreadcrumbPath(project, stage);

        // Add onClick handlers to the breadcrumb items
        const breadcrumbWithHandlers = addBreadcrumbHandlers(breadcrumbPath, project, handleProjectSelect, handleStageSelect);

        setBreadcrumbItems(breadcrumbWithHandlers);

        setIsHovering(false);
        setChartDrillDownState(null); // Reset chart drill down on stage selection
    }, [handleProjectSelect]);

    // Updates filter state and breadcrumbs when a task is selected from the hierarchy.
    const handleTaskSelect = useCallback((project, stage, task) => {
        setFilterState((prevState) => ({
            ...initialFilterFields,
            supplierId: {
                ...initialFilterFields.supplierId,
                options: prevState.supplierId.options,
            },
            selectedProjectId: { ...initialFilterFields.selectedProjectId, value: task.projectId },
            selectedStageId: { ...initialFilterFields.selectedStageId, value: stage.id },
            selectedTaskId: { ...initialFilterFields.selectedTaskId, value: task.id },
        }));
        setSubTable(null);

        // Build the complete breadcrumb path for the stage
        const stageBreadcrumbPath = buildStageBreadcrumbPath(project, stage);

        // Add onClick handlers to the breadcrumb items
        const breadcrumbWithHandlers = addBreadcrumbHandlers(stageBreadcrumbPath, project, handleProjectSelect, handleStageSelect);

        // Add the task to the breadcrumb
        breadcrumbWithHandlers.push({
            label: task.name,
            value: task.id,
            type: BREADCRUMB_ITEMS_TYPES.TASK,
            isActive: true,
            id: task.id,
            projectId: project.id, // Add projectId for hover functionality
            stageId: stage.id, // Add stageId for hover functionality
        });

        setBreadcrumbItems(breadcrumbWithHandlers);
        setIsHovering(false);
        setChartDrillDownState(null); // Reset chart drill down on task selection
    }, [handleProjectSelect, handleStageSelect]);

    const handleCloseHoverTree = useCallback(() => {
        setIsHovering(false);
        setHoveredItem(null);
    }, []);

    // Handles the hover state for the project hierarchy tree.
    // Closes the invoice details modal and clears the selected invoice.
    const handleCloseInvoiceModal = () => {
        setIsInvoiceDetailsModalOpen(false);
        setSelectedInvoice(null);
    };

    // Opens the filter modal for applying invoice filters.
    const handleOpenFilterModal = () => {
        setIsFilterModalOpen(true);
    };

    // Closes the filter modal without applying changes.
    const handleCloseFilterModal = () => {
        setIsFilterModalOpen(false);
    };

    // Handles breadcrumb clicks to navigate through the invoice management hierarchy.
    // Updates the selected grouping and resets filters and breadcrumbs.
    const handleGroupChange = useCallback((group) => {
        setSelectedGroup(group);
        setFilterState((prevState) => ({
            ...initialFilterFields,
            supplierId: {
                ...initialFilterFields.supplierId,
                options: prevState.supplierId.options,
            },
        }));
        setSubTable(null);
        setChartDrillDownState(null); // Reset chart drill-down on group change
        let breadcrumbLabel = '';
        let breadcrumbType = '';
        switch (group) {
            case INVOICE_GROUPING_VALUES.PROJECT:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.PROJECT;
                breadcrumbType = TABLE_GROUP_TYPES.PROJECT;
                break;
            case INVOICE_GROUPING_VALUES.SECTION:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.SECTION;
                breadcrumbType = TABLE_GROUP_TYPES.SECTION;
                break;
            case INVOICE_GROUPING_VALUES.PO:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.PO;
                breadcrumbType = TABLE_GROUP_TYPES.PO;
                break;
            case INVOICE_GROUPING_VALUES.SUPPLIER:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.SUPPLIER;
                breadcrumbType = TABLE_GROUP_TYPES.SUPPLIER;
                break;
            case INVOICE_GROUPING_VALUES.MONTH:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.MONTH;
                breadcrumbType = TABLE_GROUP_TYPES.MONTH;
                break;
            case INVOICE_GROUPING_VALUES.QUARTER:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.QUARTER;
                breadcrumbType = TABLE_GROUP_TYPES.QUARTER;
                break;
            case INVOICE_GROUPING_VALUES.NONE:
            default:
                breadcrumbLabel = TABLE_BREADCRUMB_ITEMS.INVOICE;
                breadcrumbType = TABLE_GROUP_TYPES.NONE;
                break;
        }
        setBreadcrumbItems([
            {
                label: breadcrumbLabel,
                type: breadcrumbType,
                isActive: true,
            },
        ]);
    }, []);

    // Handles breadcrumb hover events to show project hierarchy.
    // Updates sub-table and breadcrumbs when a project is clicked for drill-down.
    const handleProjectClick = useCallback((projectId, projectName) => {
        setSubTable({
            type: INVOICE_GROUPING_VALUES.PO,
            id: projectId,
            label: projectName,
            projectId,
        });
        setBreadcrumbItems([
            {
                label: TABLE_BREADCRUMB_ITEMS.PROJECT,
                type: TABLE_GROUP_TYPES.PROJECT,
                isActive: false,
                onClick: () => {
                    setSubTable(null);
                    setSelectedGroup(INVOICE_GROUPING_VALUES.PROJECT);
                    setFilterState((prevState) => ({
                        ...initialFilterFields,
                        supplierId: {
                            ...initialFilterFields.supplierId,
                            options: prevState.supplierId.options,
                        },
                    }));
                    setBreadcrumbItems([{ label: TABLE_BREADCRUMB_ITEMS.PROJECT, type: TABLE_GROUP_TYPES.PROJECT, isActive: true }]);
                    setChartDrillDownState(null); // Reset chart
                },
            },
            {
                label: 'PurchaseOrders',
                type: INVOICE_GROUPING_VALUES.PO,
                id: projectId,
                isActive: true,
                projectId,
            },
        ]);
        setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId, label: projectName, originalGroup: INVOICE_GROUPING_VALUES.PROJECT });
    }, [setSubTable, setBreadcrumbItems, setSelectedGroup, setFilterState]);

    // Handles breadcrumb clicks for sections, updating sub-table and breadcrumbs.
    // Updates sub-table and breadcrumbs when a section is clicked for drill-down.
    const handleSectionClick = useCallback((sectionId, sectionName, projectId, projectName) => {
        setSubTable({
            type: INVOICE_GROUPING_VALUES.PO,
            id: sectionId,
            label: sectionName,
            projectId,
            stageId: sectionId,
            projectName: projectName
        });
        setBreadcrumbItems([
            {
                label: TABLE_BREADCRUMB_ITEMS.SECTION,
                type: TABLE_GROUP_TYPES.SECTION,
                isActive: false,
                onClick: () => {
                    setSubTable(null);
                    setSelectedGroup(INVOICE_GROUPING_VALUES.SECTION);
                    setFilterState((prevState) => ({
                        ...initialFilterFields,
                        supplierId: {
                            ...initialFilterFields.supplierId,
                            options: prevState.supplierId.options,
                        },
                    }));
                    setBreadcrumbItems([{ label: TABLE_BREADCRUMB_ITEMS.SECTION, type: TABLE_GROUP_TYPES.SECTION, isActive: true }]);
                    setChartDrillDownState(null); // Reset chart
                },
            },
            {
                label: 'PurchaseOrders',
                type: INVOICE_GROUPING_VALUES.PO,
                id: sectionId,
                isActive: true,
                projectId,
                stageId: sectionId,
                projectName: projectName
            },
        ]);
        setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId, stageId: sectionId, label: sectionName, originalGroup: INVOICE_GROUPING_VALUES.SECTION });
    }, [setSubTable, setBreadcrumbItems, setSelectedGroup, setFilterState]);

    //helper function to get the stage name from the stage id
    const getStageName = (stages, stageId) => {
        for (const stage of stages) {
            let name = '';
            if (stage.id === stageId) name = stage.sectionHierarchy.replaceAll('>', '\\');
            else if (stage.stages && stage.stages.length > 0) name = getStageName(stage.stages, stageId);
            if (name) return name;
        }
    }

    // Handles breadcrumb clicks for suppliers, updating sub-table and breadcrumbs.
    // Updates sub-table and breadcrumbs when a purchase order is clicked for drill-down.
    const handlePOClick = useCallback((poId, projectId, stageId, procurementCode) => {
        setSubTable({
            type: INVOICE_GROUPING_VALUES.INVOICES,
            id: poId,
            label: poId,
            projectId,
            stageId,
        });
        const groupLabel = selectedGroup === INVOICE_GROUPING_VALUES.PROJECT ? TABLE_BREADCRUMB_ITEMS.PROJECT : selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? TABLE_BREADCRUMB_ITEMS.SECTION : TABLE_BREADCRUMB_ITEMS.PO;
        const groupType = selectedGroup === INVOICE_GROUPING_VALUES.PROJECT ? 'group-project' : selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? 'group-section' : 'group-po';
        let breadcrumbBase = [{
            label: groupLabel,
            type: groupType,
            isActive: false,
            onClick: () => {
                setSubTable(null);
                setSelectedGroup(selectedGroup);
                setFilterState((prevState) => ({
                    ...initialFilterFields,
                    supplierId: {
                        ...initialFilterFields.supplierId,
                        options: prevState.supplierId.options,
                    },
                }));
                setBreadcrumbItems([{ label: groupLabel, type: groupType, isActive: true }]);
                setChartDrillDownState(null); // Reset chart
            },
        }];
        if (selectedGroup !== INVOICE_GROUPING_VALUES.PO) {
            const poBreadcrumbId = selectedGroup === INVOICE_GROUPING_VALUES.SECTION ? stageId : projectId;
            const poBreadcrumbLabel = (selectedGroup === INVOICE_GROUPING_VALUES.PROJECT && projectId) ? projects.find(p => p.id === projectId)?.name :
                (selectedGroup === INVOICE_GROUPING_VALUES.SECTION && stageId && stageId !== 'variations') ? getStageName(projects.flatMap(p => p.stages), stageId) :
                    (selectedGroup === INVOICE_GROUPING_VALUES.SECTION && stageId && stageId === 'variations') ? `${projects.find(p => p.id === projectId)?.name}(${TaskType.VARIATION})` : procurementCode || poId;
            breadcrumbBase.push({
                label: `PurchaseOrders (${poBreadcrumbLabel})`,
                type: INVOICE_GROUPING_VALUES.PO,
                id: poBreadcrumbId,
                isActive: false,
                projectId,
                stageId,
                onClick: () => {
                    setSubTable({
                        type: INVOICE_GROUPING_VALUES.PO,
                        id: poBreadcrumbId,
                        label: poBreadcrumbLabel,
                        projectId,
                        stageId,
                    });
                    setBreadcrumbItems([
                        {
                            label: groupLabel,
                            type: groupType,
                            isActive: false,
                            onClick: () => {
                                setSubTable(null);
                                setSelectedGroup(selectedGroup);
                                setFilterState((prevState) => ({
                                    ...initialFilterFields,
                                    supplierId: {
                                        ...initialFilterFields.supplierId,
                                        options: prevState.supplierId.options,
                                    },
                                }));
                                setBreadcrumbItems([{ label: groupLabel, type: groupType, isActive: true }]);
                                setChartDrillDownState(null); // Reset chart
                            },
                        },
                        {
                            label: `PurchaseOrders (${poBreadcrumbLabel})`,
                            type: INVOICE_GROUPING_VALUES.PO,
                            id: poBreadcrumbId,
                            isActive: true,
                            projectId,
                            stageId,
                        },
                    ]);
                    setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.PO_DETAILS, projectId, stageId, label: poBreadcrumbLabel, originalGroup: selectedGroup });
                },
            });
        }
        setBreadcrumbItems([
            ...breadcrumbBase,
            {
                label: `Invoices for PO ${procurementCode || poId}`,
                type: INVOICE_GROUPING_VALUES.INVOICES,
                id: poId,
                isActive: true,
            },
        ]);
        setChartDrillDownState({ type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: poId, label: poId, originalGroup: selectedGroup, projectId, stageId });
    }, [setSubTable, setBreadcrumbItems, setSelectedGroup, setFilterState, projects, selectedGroup]);

    // Handles breadcrumb clicks for suppliers, updating sub-table and breadcrumbs.
    //  Updates sub-table and breadcrumbs when a supplier is clicked for drill-down.
    const handleSupplierClick = useCallback((supplierId, supplierName) => {
        setSubTable({
            type: INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES,
            id: supplierId,
            label: supplierName,
        });
        setBreadcrumbItems([
            {
                label: TABLE_BREADCRUMB_ITEMS.SUPPLIER,
                type: TABLE_GROUP_TYPES.SUPPLIER,
                isActive: false,
                onClick: () => {
                    setSubTable(null);
                    setSelectedGroup(INVOICE_GROUPING_VALUES.SUPPLIER);
                    setFilterState((prevState) => ({
                        ...initialFilterFields,
                        supplierId: {
                            ...initialFilterFields.supplierId,
                            options: prevState.supplierId.options,
                        },
                    }));
                    setBreadcrumbItems([{ label: TABLE_BREADCRUMB_ITEMS.SUPPLIER, type: TABLE_GROUP_TYPES.SUPPLIER, isActive: true }]);
                    setChartDrillDownState(null); // Synchronize chart
                },
            },
            {
                label: `Invoices for ${supplierName}`,
                type: INVOICE_GROUPING_VALUES.SUPPLIER_INVOICES,
                id: supplierId,
                isActive: true,
            },
        ]);
        setChartDrillDownState(prev => prev || { type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: supplierId, label: supplierName, originalGroup: INVOICE_GROUPING_VALUES.SUPPLIER });
    }, [setSubTable, setBreadcrumbItems, setSelectedGroup, setFilterState]);

    // Handles breadcrumb clicks for periods (month/quarter), updating sub-table and breadcrumbs.
    // Updates sub-table and breadcrumbs when a month or quarter period is clicked for drill-down.
    const handlePeriodClick = useCallback((periodKey, periodLabel, groupType) => {
        setSubTable({
            type: `${groupType}Invoices`,
            id: periodKey,
            label: periodLabel,
        });
        const groupTableLabel = groupType === INVOICE_GROUPING_VALUES.MONTH ? TABLE_BREADCRUMB_ITEMS.MONTH : TABLE_BREADCRUMB_ITEMS.QUARTER;
        const groupTableType = groupType === INVOICE_GROUPING_VALUES.MONTH ? TABLE_GROUP_TYPES.MONTH : TABLE_GROUP_TYPES.QUARTER;
        setBreadcrumbItems([
            {
                label: groupTableLabel,
                type: groupTableType,
                isActive: false,
                onClick: () => {
                    setSubTable(null);
                    setSelectedGroup(groupType);
                    setFilterState((prevState) => ({
                        ...initialFilterFields,
                        supplierId: {
                            ...initialFilterFields.supplierId,
                            options: prevState.supplierId.options,
                        },
                    }));
                    setBreadcrumbItems([{ label: groupTableLabel, type: groupTableType, isActive: true }]);
                    setChartDrillDownState(null); // Synchronize chart
                },
            },
            {
                label: `Invoices for ${periodLabel}`,
                type: `${groupType}Invoices`,
                id: periodKey,
                isActive: true,
            },
        ]);
        setChartDrillDownState(prev => prev || { type: INVOICE_CHART_DRILL_DOWN_STATE.INVOICE_DETAILS, id: periodKey, label: periodLabel, originalGroup: groupType });
    }, [setSubTable, setBreadcrumbItems, setSelectedGroup, setFilterState]);

    // Handles breadcrumb clicks to navigate through the invoice management hierarchy.
    // Updates the selected grouping and resets filters and breadcrumbs.
    const handleBreadcrumbClickWrapper = useCallback((item) => {
        const context = {
            projects,
            subTable,
            selectedGroup,
            handleProjectSelect,
            handleStageSelect,
            handleTaskSelect,
            handlePOClick,
            setFilterState,
            setBreadcrumbItems,
            setSubTable,
            setSelectedGroup,
            setChartDrillDownState,
            setIsHovering,
            initialFilterFields
        };

        handleBreadcrumbClick(item, context);
    }, [projects, subTable, selectedGroup, handleProjectSelect, handleStageSelect, handleTaskSelect, handlePOClick, setFilterState, setBreadcrumbItems, setSubTable, setSelectedGroup, setChartDrillDownState, setIsHovering, initialFilterFields]);

    // Handles breadcrumb hover events to show project hierarchy.
    // Sets the hovered item and opens the project hierarchy panel on breadcrumb hover.
    const handleBreadcrumbHover = (item) => {
        setHoveredItem(item);
        setIsHovering(true);
    };

    // Ensure contentHeader is updated based on subTable or selectedGroup
    const contentHeader = subTable ? (typeof CONTENT_HEADERS.SUB_TABLE[subTable.type] === 'function'
        ? CONTENT_HEADERS.SUB_TABLE[subTable.type](subTable.label) : CONTENT_HEADERS.SUB_TABLE[subTable.type])
        : CONTENT_HEADERS.GROUPED[selectedGroup] || CONTENT_HEADERS.DEFAULT;

    // Error display component
    const ErrorDisplay = ({ error, onRetry }) => (
        <div className="alert alert-danger m-3" role="alert">
            <div className="d-flex justify-content-between align-items-center">
                <div>
                    <strong>Error:</strong> {error}
                </div>
                <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={onRetry}
                >
                    Retry
                </button>
            </div>
        </div>
    );

    return (
        <>
            {projectModule ? (
                <>
                    <div className="main-content invoice-management-block">
                        {/* Error Display */}
                        {error && (
                            <ErrorDisplay
                                error={error}
                                onRetry={() => {
                                    setError(null);
                                    fetchInvoiceData();
                                }}
                            />
                        )}

                        {/* Main Content - Only show when not loading and no error */}
                        {!error && (
                            <>
                                {/*  Renders a hoverable tree for navigating projects, stages, and tasks. */}
                                <ProjectHierarchyHoverTree
                                    projects={projects}
                                    onSelectProject={handleProjectSelect}
                                    onSelectStage={handleStageSelect}
                                    onSelectTask={handleTaskSelect}
                                    onClose={handleCloseHoverTree}
                                    filterState={filterState}
                                    isHovering={isHovering}
                                    hoveredItem={hoveredItem}
                                    ref={panelRef}
                                />

                                {/*Displays breadcrumbs, summary statistics, and controls for grouping and filtering invoices. */}
                                <HeaderSection
                                    breadcrumbItems={breadcrumbItems}
                                    handleBreadcrumbHover={handleBreadcrumbHover}
                                    handleBreadcrumbClick={handleBreadcrumbClickWrapper}
                                    filterState={filterState}
                                    summaryStats={summaryStats}
                                    fetchInvoiceData={fetchInvoiceData}
                                    handleOpenFilterModal={handleOpenFilterModal}
                                    handleGroupChange={handleGroupChange}
                                    selectedGroup={selectedGroup}
                                    filteredInvoices={filteredInvoices}
                                    projects={projects}
                                    subTable={subTable}
                                    onChartBarClick={handleChartBarClick}
                                    chartDrillDownState={chartDrillDownState}
                                />
                                <div className="table layout">
                                    <div className="main-content">
                                        <div className="content-header">{contentHeader}</div>
                                        {/* Provides a modal interface for applying invoice filters like supplier, status, and date range.*/}
                                        <FilterModal
                                            isFilterModalOpen={isFilterModalOpen}
                                            handleCloseFilterModal={handleCloseFilterModal}
                                            handleApplyFiltersAndCloseModal={handleApplyFiltersAndCloseModal}
                                            filterState={filterState}
                                            handleFilterChange={handleFilterChange}
                                        />
                                        <div className="invoice-table-contain">
                                            {/* Conditionally renders the appropriate table component based on grouping or sub-table state. */}
                                            {renderTable({
                                                subTable,
                                                filteredInvoices,
                                                projects,
                                                breadcrumbItems,
                                                handleViewInvoiceDetailsModal,
                                                selectedGroup,
                                                handlePOClick,
                                                handleBreadcrumbClick: handleBreadcrumbClickWrapper,
                                                handleProjectClick,
                                                handleSectionClick,
                                                handleSupplierClick,
                                                handlePeriodClick
                                            })}
                                        </div>
                                    </div>
                                </div>
                                {isInvoiceDetailsModalOpen && selectedInvoice && (
                                    // Shows detailed information for a selected invoice in a modal.
                                    <ViewInvoiceModal
                                        show={isInvoiceDetailsModalOpen}
                                        onHide={handleCloseInvoiceModal}
                                        invoiceData={selectedInvoice}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </>
            ) : (
                <p>
                    You do not currently have access to any Projects. Please contact your Administrator if you need access.
                </p>
            )}
        </>
    );
};

export default InvoiceManagement;
